import type { ReactNode } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import { clsx } from 'clsx'
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

import styles from './RatingTrend.module.scss'

import type { RatingCounts } from '@/api/meetingSummaryFeedback'
import DataTable from '@/components/DataTable'
import { percent } from '@/utils'
import { DAY_MS, utcDay } from '@/utils/dates'

export type RatingBucket = 'day' | 'week'
export type RatingSeries = {
  key: string
  label: string
  color?: string
  stats: { overall: RatingCounts; buckets: (RatingCounts & { start: string })[] } | undefined
}

const ZERO: RatingCounts = { up: 0, down: 0, total: 0, pctUp: null }

export const bucketStarts = (since: string, until: string, bucket: RatingBucket) => {
  const start = new Date(since)
  start.setUTCHours(0, 0, 0, 0)
  if (bucket === 'week') start.setUTCDate(start.getUTCDate() - ((start.getUTCDay() + 6) % 7))
  const step = bucket === 'week' ? 7 * DAY_MS : DAY_MS
  const starts: string[] = []
  for (let ms = start.getTime(); ms < Date.parse(until); ms += step) {
    starts.push(utcDay(ms))
  }
  return starts
}

export const periodLabel = (period: string, bucket: RatingBucket) => (bucket === 'week' ? 'Week of ' + period : period)

type PanelProps = {
  title: string
  ariaLabel?: string
  description: string
  bucket: RatingBucket
  onBucket: (bucket: RatingBucket) => void
  isDayAllowed?: boolean
  controls?: ReactNode
  children: ReactNode
}

export const TrendPanel = ({
  title,
  ariaLabel = title,
  description,
  bucket,
  onBucket,
  isDayAllowed = true,
  controls,
  children,
}: PanelProps) => (
  <section className={styles.panel} aria-label={ariaLabel}>
    <div className={styles.header}>
      <div>
        <h2>{title}</h2>
        <span className={styles.muted}>
          {bucket === 'week' ? 'Weekly (Monday starts)' : 'Daily'} · UTC · {description}
        </span>
      </div>
      <div className={styles.segmented} role="group" aria-label="Group by">
        {(['day', 'week'] as const).map((value) => (
          <button
            key={value}
            type="button"
            aria-pressed={bucket === value}
            disabled={value === 'day' && !isDayAllowed}
            className={clsx(styles.segment, bucket === value && styles.active)}
            onClick={() => onBucket(value)}
          >
            {value === 'day' ? 'By day' : 'By week'}
          </button>
        ))}
      </div>
    </div>
    {controls}
    {children}
  </section>
)

type Row = { period: string; counts: RatingCounts[] }
type Props = {
  since: string
  until: string
  bucket: RatingBucket
  onBucket: (bucket: RatingBucket) => void
  series: RatingSeries[]
  isDayAllowed?: boolean
  description?: string
  controls?: ReactNode
}

const RatingTrend = ({
  since,
  until,
  bucket,
  onBucket,
  series,
  isDayAllowed = true,
  description = 'current filters',
  controls,
}: Props) => {
  const lookups = series.map((item) => new Map(item.stats?.buckets.map((row) => [utcDay(row.start), row])))
  const rows: Row[] = bucketStarts(since, until, bucket).map((period) => ({
    period,
    counts: lookups.map((lookup) => lookup.get(period) ?? ZERO),
  }))
  const points = rows.map(({ period, counts }) =>
    Object.fromEntries([['period', period], ...series.map((item, index) => [item.key, counts[index].pctUp])])
  )
  const isLoading = series.some((item) => !item.stats)
  const hasRatings = series.some((item) => (item.stats?.overall.total ?? 0) > 0)
  const isWeekly = bucket === 'week'
  const prefix = (item: RatingSeries) => (series.length > 1 ? item.label + ' ' : '')
  const columns: ColumnDef<Row>[] = [
    {
      id: 'period',
      header: isWeekly ? 'Week (UTC)' : 'Day (UTC)',
      accessorFn: (row) => periodLabel(row.period, bucket),
    },
    ...series.flatMap((item, index): ColumnDef<Row>[] => [
      { id: item.key + '-up', header: prefix(item) + '👍', accessorFn: (row) => row.counts[index].up },
      { id: item.key + '-down', header: prefix(item) + '👎', accessorFn: (row) => row.counts[index].down },
      { id: item.key + '-total', header: prefix(item) + 'Total', accessorFn: (row) => row.counts[index].total },
      {
        id: item.key + '-pct',
        header: prefix(item) + '% up',
        accessorFn: (row) => row.counts[index].pctUp ?? -1,
        cell: ({ row }) => percent(row.original.counts[index].pctUp),
      },
    ]),
  ]

  let body
  if (isLoading) {
    body = (
      <p className={styles.empty} role="status">
        Loading rates…
      </p>
    )
  } else if (!hasRatings) {
    body = <p className={styles.empty}>No ratings in this range.</p>
  } else {
    body = (
      <>
        <div className={styles.chart}>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={points} margin={{ top: 12, right: 24, bottom: 0, left: 0 }} accessibilityLayer>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="period" tickFormatter={(value: string) => value.slice(5)} />
              <YAxis domain={[0, 100]} tickFormatter={(value: number) => value + '%'} width={56} />
              <Tooltip
                labelFormatter={(label) => periodLabel(String(label), bucket) + ' (UTC)'}
                formatter={(value) => (typeof value === 'number' ? value.toFixed(1) + '%' : 'No ratings')}
              />
              <Legend />
              {series.map((item) => (
                <Line
                  key={item.key}
                  dataKey={item.key}
                  name={item.label}
                  stroke={item.color ?? 'var(--rating-series-color)'}
                  strokeWidth={2}
                  dot={{ r: 2 }}
                  isAnimationActive={false}
                  connectNulls={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
        <details className={styles.detail}>
          <summary>{isWeekly ? 'View weekly counts' : 'View daily counts'}</summary>
          <DataTable data={[...rows].reverse()} columns={columns} ariaLabel="Rating counts by period" />
        </details>
      </>
    )
  }

  return (
    <TrendPanel
      title="👍 rate over time"
      ariaLabel="Thumbs-up rate over time"
      description={description}
      bucket={bucket}
      onBucket={onBucket}
      isDayAllowed={isDayAllowed}
      controls={controls}
    >
      {body}
    </TrendPanel>
  )
}

export default RatingTrend
